<template>
  <!-- Security & continuity tab: criticality, recovery targets, measures by category and policies (US 9370) -->
  <div v-if="data" class="sx-security" qid="system-security-tab">
    <div class="sx-kpis">
      <div class="sx-kpi" :class="{ 'is-empty': !classified }">
        <small>{{ $t('systems.assessments.system_classification_title') }}</small>
        <template v-if="classified">
          <div class="sp-cia">
            <span v-for="key in ['confidentiality', 'integrity', 'availability']" :key="key" :title="$t('systems_overview.criticality.' + key)">
              <b :class="ciaClass(data.criticality[key])">{{ impactLevel(data.criticality[key]) || '–' }}</b>{{ $t('systems_overview.criticality.' + key).slice(0, 1) }}
            </span>
          </div>
          <span>{{ $t('system_security.classification_hint') }}</span>
        </template>
        <template v-else>
          <span class="sx-kpi__none">{{ $t('system_assessments.not_classified') }}</span>
          <button v-if="getClient.assessmentModule" type="button" class="v2-link sx-kpi__cta" qid="system-security-classify" @click="$emit('open-tab', 'assessments')">
            {{ $t('system_security.classify_on_assessments') }} <font-awesome-icon icon="arrow-right"/>
          </button>
        </template>
      </div>
      <div v-for="key in ['rto', 'rpo']" :key="key" class="sx-kpi" :class="{ 'is-empty': !data[key] }">
        <small>{{ $t('system_security.' + key) }}</small>
        <b v-if="data[key]">{{ data[key].number + ' ' + data[key].type }}</b>
        <span v-else class="sx-kpi__none">{{ $t('system_security.not_set') }}</span>
        <span>{{ $t('system_security.' + key + '_hint') }}</span>
      </div>
      <div class="sx-kpi" :class="{ 'is-empty': !data.counts.total }">
        <small>{{ $t('system_page.security.measures') }}</small>
        <template v-if="data.counts.total">
          <b>{{ $t('system_security.in_place_of', { count: data.counts.in_place, total: data.counts.total }) }}</b>
          <span>{{ measuresHint }}</span>
        </template>
        <template v-else>
          <span class="sx-kpi__none">{{ $t('system_security.no_measures_short') }}</span>
          <button v-if="canEdit" type="button" class="v2-link sx-kpi__cta" @click="$refs.measurePanel.show()">
            <font-awesome-icon icon="plus"/>{{ $t('system_security.add_measure') }}
          </button>
        </template>
      </div>
    </div>

    <div class="sp-overview">
      <section class="v2-card sp-sec sx-measures">
        <div class="sp-sec__head">
          <font-awesome-icon icon="shield-alt"/>
          <h3>{{ $t('system_security.measures_title') }}</h3>
          <span class="sp-sec__state">{{ $t('system_security.grouped') }}</span>
          <span class="sp-grow"></span>
          <b-form-input v-if="data.measures.length > 5"
                        v-model="filter"
                        type="search"
                        size="sm"
                        class="sx-measures__filter"
                        :placeholder="$t('system_security.filter')"
                        qid="system-measures-filter"></b-form-input>
          <b-button v-if="canEdit" size="sm" variant="success" qid="system-add-measure" @click="$refs.measurePanel.show()">
            <font-awesome-icon icon="plus"/> {{ $t('system_security.add_measure') }}
          </b-button>
        </div>

        <template v-for="group in groups">
          <div :key="'h' + group.name" class="sp-group">
            {{ group.name || $t('system_security.no_category') }}
            <span class="sp-grow"></span>
            {{ $t('system_security.in_place_of', { count: group.inPlace, total: group.items.length }) }}
          </div>
          <div v-for="measure in group.items" :key="measure.link_id" class="sx-li" :qid="'system-measure-' + measure.link_id">
            <div>
              <div class="sx-li__t">{{ measure.name }}</div>
              <div v-if="measure.description" class="sx-li__d">{{ measure.description | truncate(140) }}</div>
            </div>
            <div>
              <span class="sx-st" :class="'is-' + measure.status">
                <font-awesome-icon :icon="statusIcon[measure.status]"/>{{ $t('system_security.status.' + measure.status) }}
              </span>
              <div v-if="measure.response && measure.status !== 'unanswered'" class="sx-li__d">{{ measure.response }}</div>
            </div>
            <div class="sx-li__d">{{ formatDate(measure.updated_at) }}</div>
            <b-dropdown v-if="canEdit" right no-caret variant="link" toggle-class="sp-contract__more" menu-class="sx-menu">
              <template #button-content><font-awesome-icon icon="ellipsis-h"/><span class="sr-only">{{ $t('system_hero.more') }}</span></template>
              <b-dropdown-item @click="$refs.measurePanel.show(measure)"><font-awesome-icon icon="pen"/>{{ $t('system_security.answer') }}</b-dropdown-item>
              <b-dropdown-item link-class="is-danger" @click="removeMeasure(measure)"><font-awesome-icon icon="unlink"/>{{ $t('system_link.unlink') }}</b-dropdown-item>
            </b-dropdown>
            <span v-else></span>
          </div>
        </template>
        <p v-if="!data.measures.length" class="sx-empty">{{ $t('system_security.no_measures') }}</p>
        <p v-else-if="!groups.length" class="sx-empty">{{ $t('systems_overview.empty_search') }}</p>
      </section>

      <div class="sp-side">
        <section class="v2-card sp-side__card" qid="system-security-source">
          <h4><font-awesome-icon icon="chart-line"/>{{ $t('system_security.source_title') }}</h4>
          <div class="sp-row">
            <span>{{ $t('system_security.source_label') }}</span>
            <router-link v-if="data.source" :to="{ name: 'projects.view', params: { slug: data.source.slug } }">{{ data.source.title }}</router-link>
            <b v-else>{{ $t('system_security.source_none') }}</b>
          </div>
          <div v-if="data.source" class="sp-row">
            <span>{{ $t('system_security.finished') }}</span>
            <b>{{ formatDate(data.source.completed_at) }}</b>
          </div>
          <small class="sp-sub">{{ $t('system_security.source_hint') }}</small>
          <button v-if="getClient.assessmentModule" type="button" class="v2-link sp-open" @click="$emit('open-tab', 'assessments')">
            {{ $t('system_security.open_assessments') }} <font-awesome-icon icon="arrow-right"/>
          </button>
        </section>

        <section v-if="data.policies" class="v2-card sp-side__card" qid="system-policies">
          <h4><font-awesome-icon icon="file-alt"/>{{ $t('system_security.policies_title') }}</h4>
          <div v-for="policy in data.policies" :key="policy.link_id" class="sp-row sx-policy">
            <router-link :to="{ name: 'policies.view', params: { slug: policy.slug } }">{{ policy.name }}</router-link>
            <span v-if="policy.implementation_status" class="sx-st" :class="policyClass(policy.implementation_status)">
              {{ $t('maintenance.policies.status.' + $options.filters.slugify(policy.implementation_status)) }}
            </span>
            <button v-if="canEdit" type="button" class="sx-policy__x" :aria-label="$t('system_link.unlink')" @click="removePolicy(policy)">
              <font-awesome-icon icon="times"/>
            </button>
          </div>
          <p v-if="!data.policies.length" class="sp-sub">{{ $t('system_security.no_policies') }}</p>
          <button v-if="canEdit" type="button" class="v2-link sp-open" qid="system-link-policy" @click="openPolicy">
            <font-awesome-icon icon="plus"/> {{ $t('system_security.link_policy') }}
          </button>
        </section>
      </div>
    </div>

    <SystemMeasurePanel ref="measurePanel" :system="system" :linked="data.measures" @saved="changed"></SystemMeasurePanel>

    <V2SidePanel v-if="data.policies"
                 ref="policyPanel"
                 :kind="$t('system_security.policy_kind')"
                 :title="$t('system_security.link_policy')"
                 :intro="$t('system_security.policy_intro', { name: system.name })"
                 :busy="saving"
                 qid="system-policy-panel">
      <b-form-input v-model="policyKeyword" type="search" :placeholder="$t('system_security.policy_search')" @input="searchPolicies"></b-form-input>
      <div class="sx-options">
        <button v-for="policy in policyOptions"
                :key="policy.id"
                type="button"
                class="sx-option"
                :class="{ on: selectedPolicy && selectedPolicy.id === policy.id }"
                :disabled="linkedPolicyIds.includes(policy.id)"
                @click="selectedPolicy = policy">
          <font-awesome-icon icon="file-alt"/>
          <span class="sx-option__grow">{{ policy.name }}<small v-if="policy.category">{{ policy.category }}</small></span>
          <font-awesome-icon v-if="selectedPolicy && selectedPolicy.id === policy.id" icon="check"/>
        </button>
      </div>
      <template #foot>
        <span class="v2-panel__grow"></span>
        <b-button variant="secondary" @click="$refs.policyPanel.hide()">{{ $t('systems.create.cancel') }}</b-button>
        <b-button variant="success" :disabled="saving || !selectedPolicy" qid="system-policy-submit" @click="addPolicy">
          <font-awesome-icon icon="link"/> {{ $t('system_security.link_policy') }}
        </b-button>
      </template>
    </V2SidePanel>
  </div>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';
  import {impactClass, impactLevel, impactKey} from '@/util/impactLevels.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import SystemMeasurePanel from '@/views/Systems/page/SystemMeasurePanel.vue';

  export default {
    name: 'SystemSecurityTab',
    components: {
      V2SidePanel,
      SystemMeasurePanel
    },
    props: {
      system: { type: Object, required: true }
    },
    data: function() {
      return {
        data: null,
        filter: '',
        policyKeyword: '',
        policyOptions: [],
        selectedPolicy: null,
        saving: false,
        searchTimer: null,
        statusIcon: { in_place: 'check', not_in_place: 'exclamation-triangle', unanswered: 'clock' }
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      canEdit() {
        return this.isGranted([this.permissions.SYSTEM_EDIT, this.permissions.SYSTEM_EDIT_OWN], this.system) && (!this.getClient.parent || this.system.partition);
      },
      classified() {
        return ['confidentiality', 'integrity', 'availability'].some(key => this.data.criticality[key]);
      },
      measuresHint() {
        let counts = this.data.counts;
        let parts = [];
        if (counts.not_in_place) {
          parts.push(this.$tc('system_security.count_not_in_place', counts.not_in_place, { count: counts.not_in_place }));
        }
        if (counts.unanswered) {
          parts.push(this.$tc('system_security.count_unanswered', counts.unanswered, { count: counts.unanswered }));
        }
        return parts.join(' · ') || (counts.total ? this.$t('system_security.all_in_place') : this.$t('system_security.no_measures_short'));
      },
      groups() {
        let keyword = this.filter.trim().toLowerCase();
        let groups = [];
        this.data.measures
          .filter(measure => !keyword || (measure.name + ' ' + (measure.description || '') + ' ' + (measure.category || '')).toLowerCase().includes(keyword))
          .forEach(function(measure) {
            let group = groups.find(item => item.name === measure.category);
            if (!group) {
              group = { name: measure.category, items: [], inPlace: 0 };
              groups.push(group);
            }
            group.items.push(measure);
            group.inPlace += measure.status === 'in_place' ? 1 : 0;
          });
        return groups;
      },
      linkedPolicyIds() {
        return (this.data.policies || []).map(policy => policy.id);
      }
    },
    watch: {
      system: function(value, old) {
        if (!old || value.updated_at !== old.updated_at) {
          this.load();
        }
      }
    },
    methods: {
      load() {
        let self = this;
        piincHttp.get('systems/' + this.system.slug + '/security', { params: { client: this.getClient.slug } }).then(function(response) {
          self.data = response.data;
        }, function() {});
      },
      changed() {
        this.load();
        this.$emit('updated');
      },
      formatDate(value) {
        return value ? moment(value).format('D MMM YYYY') : '';
      },
      // Stored as text levels ("Very low"…"Very high"), shown as 1–4
      ciaClass: impactClass,
      impactLevel: impactLevel,
      impactKey: impactKey,
      policyClass(status) {
        let key = this.$options.filters.slugify(status);
        return key === 'fully_implemented' ? 'is-in_place' : (key === 'not_designed' ? 'is-not_in_place' : 'is-partly');
      },
      confirm(text, title) {
        return this.$bvModal.msgBoxConfirm(text, {
          title: title,
          okTitle: this.$t('system_link.unlink'),
          okVariant: 'danger',
          cancelTitle: this.$t('systems.create.cancel'),
          centered: true
        });
      },
      removeMeasure(measure) {
        let self = this;
        this.confirm(this.$t('system_security.unlink_measure_text', { measure: measure.name, system: this.system.name }), this.$t('system_security.unlink_measure_title')).then(function(ok) {
          if (ok) {
            piincHttp.delete('systems/' + self.system.slug + '/measures/' + measure.link_id).then(function() {
              self.$toastr('success', self.$t('activities.measures.delete.success'));
              self.changed();
            }, function() {});
          }
        });
      },
      openPolicy() {
        this.selectedPolicy = null;
        this.policyKeyword = '';
        this.loadPolicies();
        this.$refs.policyPanel.show();
      },
      searchPolicies() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.loadPolicies, 300);
      },
      loadPolicies() {
        let self = this;
        piincHttp.get('policies', { params: { client: this.system.client || this.getClient.slug, per_page: 50, keyword: this.policyKeyword || undefined } }).then(function(response) {
          self.policyOptions = response.data.items;
        }, function() {});
      },
      addPolicy() {
        let self = this;
        this.saving = true;
        piincHttp.post('systems/' + this.system.slug + '/policies', { policy_id: this.selectedPolicy.id, system: this.system.slug }).then(function() {
          self.saving = false;
          self.$refs.policyPanel.hide();
          self.$toastr('success', self.$t('systems.policies.create.success'));
          self.changed();
        }, function() {
          self.saving = false;
        });
      },
      removePolicy(policy) {
        let self = this;
        this.confirm(this.$t('system_security.unlink_policy_text', { policy: policy.name, system: this.system.name }), this.$t('system_security.unlink_policy_title')).then(function(ok) {
          if (ok) {
            piincHttp.delete('systems/' + self.system.slug + '/policies/' + policy.link_id).then(function() {
              self.$toastr('success', self.$t('systems.policies.delete.success'));
              self.changed();
            }, function() {});
          }
        });
      }
    },
    created() {
      this.load();
    }
  }
</script>
